import { BACK_COMPONENT } from '@/utils/attrNameData';
import { getComponentsByType, deepCopy, uuid } from '@/utils/utils';
import {
  buildCanvasContext,
  findComponentByRef,
  findComponentIndex,
  collectLiveTargets,
  idsEqual,
} from '../context';
import {
  createComponent,
  getNextZIndex,
  clampPosition,
  resolveComponentType,
} from '../componentFactory';

function ok(data, message) {
  return { ok: true, data, message };
}

function fail(message) {
  return { ok: false, message };
}

function getBack(store) {
  return (store.state.componentData || []).find((c) => c.component === 'Back');
}

function selectComponent(store, ref, ctx) {
  const live = collectLiveTargets(store, ctx && ctx.pinnedAreaIds);
  const matched = live.find((c) => idsEqual(c.id, ref));
  const target = matched || (ref != null && ref !== "" ? findComponentByRef(store, ref) : null) || live[0] || null;
  if (!target) {
    return fail("请先在画布上点选或框选要修改的组件");
  }
  const idx = findComponentIndex(store, target.id);
  if (idx >= 0) {
    const component = store.state.componentData[idx];
    store.commit("setCurComponent", { component, index: idx });
    return ok({ id: component.id, index: idx });
  }
  store.commit("setCurComponent", { component: target, index: null });
  return ok({ id: target.id, index: -1 });
}

function requireSelected(store, ref, ctx) {
  const live = collectLiveTargets(store, ctx && ctx.pinnedAreaIds);
  let comp = null;
  if (ref != null && ref !== "") {
    comp = live.find((c) => idsEqual(c.id, ref)) || findComponentByRef(store, ref);
  }
  if (!comp && live.length) {
    comp = live[0];
  }
  if (!comp) {
    return { error: fail("请先在画布上点选或框选要修改的组件") };
  }
  const index = findComponentIndex(store, comp.id);
  if (index >= 0) {
    const liveComp = store.state.componentData[index];
    store.commit("setCurComponent", { component: liveComp, index });
    return { comp: liveComp, index };
  }
  store.commit("setCurComponent", { component: comp, index: null });
  return { comp, index: -1 };
}

function getSelectedComponents(store, ctx) {
  return collectLiveTargets(store, ctx && ctx.pinnedAreaIds);
}

function resolveRefs(store, refs, ctx) {
  if (!refs || !refs.length) {
    return getSelectedComponents(store, ctx).map((c) => c.id);
  }
  const mapped = refs
    .map((r) => findComponentByRef(store, r))
    .filter(Boolean)
    .map((c) => c.id);
  if (mapped.length) {
    return mapped;
  }
  return getSelectedComponents(store, ctx).map((c) => c.id);
}

function restoreAreaVisual(ctx, ids) {
  if (!ids || !ids.length || !ctx.hooks.restoreAreaVisual) return;
  ctx.hooks.restoreAreaVisual(ids);
}

function applyGroupAlign(ctx, args, alignKey) {
  const ids = resolveRefs(ctx.store, args.refs, ctx);
  const comps = ids
    .map((id) => findComponentByRef(ctx.store, id))
    .filter((c) => c && c.id !== 'Back');
  if (comps.length < 2) {
    return fail('请先框选至少 2 个组件再对齐');
  }
  ctx.store.commit('setAreaData', {
    style: (ctx.store.state.areaData && ctx.store.state.areaData.style) || {},
    components: comps,
  });
  ctx.store.commit('componentsAlign', alignKey);
  restoreAreaVisual(ctx, comps.map((c) => c.id));
  return ok({ count: comps.length, align: alignKey });
}

function applyDistribute(ctx, args, mutation) {
  const ids = resolveRefs(ctx.store, args.refs, ctx);
  const comps = ids
    .map((id) => findComponentByRef(ctx.store, id))
    .filter((c) => c && c.id !== 'Back');
  if (comps.length < 3) {
    return fail('请先框选至少 3 个组件再分布');
  }
  ctx.store.commit('setAreaData', {
    style: (ctx.store.state.areaData && ctx.store.state.areaData.style) || {},
    components: comps,
  });
  ctx.store.commit(mutation);
  restoreAreaVisual(ctx, comps.map((c) => c.id));
  return ok({ count: comps.length });
}

function setNested(obj, path, value) {
  const keys = path.split('.');
  let cur = obj;
  for (let i = 0; i < keys.length - 1; i++) {
    if (cur[keys[i]] === undefined) cur[keys[i]] = {};
    cur = cur[keys[i]];
  }
  cur[keys[keys.length - 1]] = value;
}

function applyPatch(target, patch) {
  Object.keys(patch || {}).forEach((key) => {
    if (patch[key] !== null && typeof patch[key] === 'object' && !Array.isArray(patch[key])) {
      if (!target[key] || typeof target[key] !== 'object') target[key] = {};
      applyPatch(target[key], patch[key]);
    } else {
      target[key] = patch[key];
    }
  });
}

function checkButtonQuota(ctx, addCount = 1) {
  const limits = ctx.hooks.getRouteLimits ? ctx.hooks.getRouteLimits() : {};
  const max = limits.btns != null ? limits.btns * 1 : Infinity;
  const current = ctx.hooks.getButtonCountEstimate
    ? ctx.hooks.getButtonCountEstimate()
    : getComponentsByType('v-button', ctx.store.state.componentData).length;
  if (current + addCount > max) {
    return fail(`按钮数量将超过上限（当前 ${current}，上限 ${max}）`);
  }
  return ok({ current, max });
}

function addComponentByType(ctx, typeKey, args = {}) {
  const quota = checkButtonQuota(ctx, typeKey.indexOf('button') >= 0 ? 1 : 0);
  if (!quota.ok && typeKey.indexOf('button') >= 0) return quota;

  const canvasStyle = ctx.hooks.getCanvasStyle
    ? ctx.hooks.getCanvasStyle()
    : ctx.store.state.canvasStyleData;
  let component = createComponent(typeKey, args);
  component.style.zIndex = getNextZIndex(ctx.store.state.componentData);
  component = clampPosition(component, canvasStyle);
  ctx.store.commit('addComponent', { component });
  ctx.store.commit('setCurComponent', {
    component,
    index: ctx.store.state.componentData.length - 1,
  });
  if (args.url && component.style) component.style.url = args.url;
  ctx.lastCreatedId = component.id;
  return ok({ id: component.id, component: component.component, alias: component.style && component.style.alias });
}

const HANDLERS = {
  // --- Context ---
  'context.get_current_page'(args, ctx) {
    const state = ctx.store.state;
    const back = getBack(ctx.store);
    return ok({
      pageId: state.selectedCurrentPage,
      pageName: back && back.style.pageName,
      componentCount: (state.componentData || []).length - 1,
    });
  },
  'context.get_project_limits'(args, ctx) {
    return ok(ctx.hooks.getRouteLimits ? ctx.hooks.getRouteLimits() : {});
  },
  'context.list_components'(args, ctx) {
    return ok(buildCanvasContext(ctx.store, ctx.hooks).components);
  },
  'context.get_component'(args, ctx) {
    const live = collectLiveTargets(ctx.store, ctx.pinnedAreaIds);
    const comp = (live.length && live[0]) || findComponentByRef(ctx.store, args.ref);
    return comp ? ok(deepCopy(comp)) : fail('请先在画布上点选或框选要修改的组件');
  },
  'context.list_pages'(args, ctx) {
    return ok(ctx.store.state.pageOptions);
  },
  'context.query_buttons'(args, ctx) {
    const btns = getComponentsByType(ctx.store.state.componentData, 'v-button');
    return ok(btns.map((b) => ({
      id: b.id,
      alias: b.style && b.style.alias,
      text: b.propValue,
      left: b.style.left,
      top: b.style.top,
    })));
  },
  'context.get_selection'(args, ctx) {
    const selected = getSelectedComponents(ctx.store, ctx).map((c) => ({
      id: c.id,
      component: c.component,
      alias: c.style && c.style.alias,
    }));
    return ok({
      current: ctx.store.state.curComponent && ctx.store.state.curComponent.id !== 'Back'
        ? selected[0] || null
        : null,
      area: selected,
    });
  },
  'context.check_quota'(args, ctx) {
    return checkButtonQuota(ctx, args.count || 1);
  },

  // --- Page ---
  'page.switch'(args, ctx) {
    if (ctx.hooks.pageSelect) {
      ctx.hooks.pageSelect(args.pageId, args.parentId);
      return ok({ pageId: args.pageId });
    }
    ctx.store.commit('setSelectedCurrentPage', args.pageId);
    return ok({ pageId: args.pageId });
  },
  'page.create_main'(args, ctx) {
    if (ctx.hooks.addNewPage) ctx.hooks.addNewPage();
    return ok({});
  },
  'page.create_child'(args, ctx) {
    if (ctx.hooks.addChildPage) ctx.hooks.addChildPage();
    return ok({});
  },
  'page.copy'(args, ctx) {
    if (ctx.hooks.copyPage) ctx.hooks.copyPage();
    return ok({});
  },
  'page.delete'(args, ctx) {
    if (ctx.hooks.deletePage) ctx.hooks.deletePage(args.pageId);
    return ok({});
  },

  // --- Back ---
  'back.set_name'(args, ctx) {
    const back = getBack(ctx.store);
    if (!back) return fail('未找到背景组件');
    back.style.pageName = args.name;
    ctx.store.commit('setCurrPageName', args.name);
    return ok({});
  },
  'back.set_background_color'(args, ctx) {
    const back = getBack(ctx.store);
    if (!back) return fail('未找到背景组件');
    back.style.backgroundColor = args.color;
    return ok({});
  },
  'back.set_background_image'(args, ctx) {
    const back = getBack(ctx.store);
    if (!back) return fail('未找到背景组件');
    back.style.backPicSrc = args.filePath || args.fileId;
    return ok({});
  },
  'back.set_grid'(args, ctx) {
    const back = getBack(ctx.store);
    if (!back) return fail('未找到背景组件');
    back.style.grid = !!args.enabled;
    return ok({});
  },
  'back.set_page_size'(args, ctx) {
    const back = getBack(ctx.store);
    if (!back) return fail('未找到背景组件');
    if (args.width != null) back.style.pageWidth = args.width;
    if (args.height != null) back.style.pageHeight = args.height;
    return ok({});
  },
  'back.set_menu_visible'(args, ctx) {
    const back = getBack(ctx.store);
    if (!back) return fail('未找到背景组件');
    back.style.isShowMenu = args.visible ? 'true' : 'false';
    return ok({});
  },
  'back.set_opacity'(args, ctx) {
    const back = getBack(ctx.store);
    if (!back) return fail('未找到背景组件');
    back.style.opacity = args.opacity;
    return ok({});
  },

  // --- Components ---
  'component.add'(args, ctx) {
    if (!resolveComponentType(args.type)) return fail(`未知类型: ${args.type}`);
    return addComponentByType(ctx, args.type, args);
  },
  'component.add_text'(args, ctx) { return addComponentByType(ctx, 'text', args); },
  'component.add_line'(args, ctx) { return addComponentByType(ctx, 'line', args); },
  'component.add_button_square'(args, ctx) { return addComponentByType(ctx, 'button_square', args); },
  'component.add_button_round'(args, ctx) { return addComponentByType(ctx, 'button_round', args); },
  'component.add_button_round_angle'(args, ctx) { return addComponentByType(ctx, 'button_round_angle', args); },
  'component.add_picture'(args, ctx) { return addComponentByType(ctx, 'picture', args); },
  'component.add_stream_media'(args, ctx) { return addComponentByType(ctx, 'stream_media', args); },
  'component.add_video'(args, ctx) { return addComponentByType(ctx, 'video', args); },
  'component.add_music'(args, ctx) { return addComponentByType(ctx, 'music', args); },
  'component.add_weather'(args, ctx) { return addComponentByType(ctx, 'weather', args); },
  'component.add_carousel'(args, ctx) { return addComponentByType(ctx, 'carousel', args); },
  'component.add_date'(args, ctx) { return addComponentByType(ctx, 'date', args); },
  'component.add_environment'(args, ctx) { return addComponentByType(ctx, 'environment', args); },
  'component.add_document'(args, ctx) { return addComponentByType(ctx, 'document', args); },
  'component.add_webpage'(args, ctx) { return addComponentByType(ctx, 'webpage', args); },
  'component.add_slider'(args, ctx) { return addComponentByType(ctx, 'slider', args); },
  'component.add_status_light'(args, ctx) { return addComponentByType(ctx, 'status_light', args); },
  'component.add_child_page'(args, ctx) { return addComponentByType(ctx, 'child_page', args); },
  'component.add_visualization'(args, ctx) { return addComponentByType(ctx, 'visualization', args); },

  'component.delete'(args, ctx) {
    const sel = requireSelected(ctx.store, args.ref, ctx);
    if (sel.error) return sel.error;
    if (sel.index < 0) return fail('请先在画布上点选或框选要修改的组件');
    ctx.store.commit('deleteComponent', sel.index);
    return ok({});
  },
  'component.delete_batch'(args, ctx) {
    const ids = resolveRefs(ctx.store, args.refs, ctx);
    ctx.store.commit('batchDeleteComponent', { deleteData: ids.map((id) => ({ id })) });
    return ok({ deleted: ids.length });
  },
  'component.select'(args, ctx) {
    return selectComponent(ctx.store, args.ref, ctx);
  },
  'component.select_by_alias'(args, ctx) {
    return selectComponent(ctx.store, args.alias, ctx);
  },
  'component.clear_canvas'(args, ctx) {
    const back = getBack(ctx.store) || BACK_COMPONENT();
    ctx.store.commit('setComponentData', [back]);
    ctx.store.commit('setCurComponent', { component: null, index: null });
    return ok({});
  },
  'component.copy'(args, ctx) {
    ctx.store.commit('copy');
    return ok({});
  },
  'component.paste'(args, ctx) {
    ctx.store.commit('paste', args.isMouse);
    return ok({});
  },
  'component.duplicate'(args, ctx) {
    const sel = requireSelected(ctx.store, args.ref, ctx);
    if (sel.error) return sel.error;
    ctx.store.commit('copy');
    ctx.store.commit('paste');
    const dx = args.offsetX != null ? args.offsetX : 20;
    const dy = args.offsetY != null ? args.offsetY : 20;
    const cur = ctx.store.state.curComponent;
    if (cur) {
      ctx.store.commit('setShapeStyle', {
        top: (cur.style.top || 0) + dy,
        left: (cur.style.left || 0) + dx,
      });
    }
    return ok({ id: ctx.store.state.curComponent && ctx.store.state.curComponent.id });
  },

  // --- Layout ---
  'layout.move'(args, ctx) {
    const sel = requireSelected(ctx.store, args.ref, ctx);
    if (sel.error) return sel.error;
    ctx.store.commit('setShapeStyle', { top: args.top, left: args.left });
    return ok({});
  },
  'layout.resize'(args, ctx) {
    const sel = requireSelected(ctx.store, args.ref, ctx);
    if (sel.error) return sel.error;
    ctx.store.commit('setShapeStyle', { width: args.width, height: args.height });
    return ok({});
  },
  'layout.set_position_box'(args, ctx) {
    const sel = requireSelected(ctx.store, args.ref, ctx);
    if (sel.error) return sel.error;
    ctx.store.commit('setShapeStyle', {
      left: args.left,
      top: args.top,
      width: args.width,
      height: args.height,
    });
    return ok({});
  },
  'layout.align_left'(args, ctx) {
    return applyGroupAlign(ctx, args, 'left');
  },
  'layout.align_right'(args, ctx) {
    return applyGroupAlign(ctx, args, 'right');
  },
  'layout.align_top'(args, ctx) {
    return applyGroupAlign(ctx, args, 'top');
  },
  'layout.align_bottom'(args, ctx) {
    return applyGroupAlign(ctx, args, 'bootom');
  },
  'layout.align_vertical_center'(args, ctx) {
    const ids = resolveRefs(ctx.store, args.refs, ctx);
    const comps = ids
      .map((id) => findComponentByRef(ctx.store, id))
      .filter((c) => c && c.id !== 'Back');
    if (comps.length < 2) {
      return fail('请先框选至少 2 个组件再对齐');
    }
    let minTop = Infinity;
    let maxBottom = -Infinity;
    comps.forEach((c) => {
      const top = Number(c.style.top) || 0;
      const height = Number(c.style.height) || 0;
      if (top < minTop) minTop = top;
      if (top + height > maxBottom) maxBottom = top + height;
    });
    const mid = (minTop + maxBottom) / 2;
    comps.forEach((c) => {
      c.style.top = mid - (Number(c.style.height) || 0) / 2;
    });
    restoreAreaVisual(ctx, comps.map((c) => c.id));
    return ok({ count: comps.length });
  },
  'layout.distribute_horizontal'(args, ctx) {
    return applyDistribute(ctx, args, 'horizontalEqual');
  },
  'layout.distribute_vertical'(args, ctx) {
    return applyDistribute(ctx, args, 'verticalEqual');
  },
  'layout.grid_place'(args, ctx) {
    const ids = resolveRefs(ctx.store, args.refs, ctx);
    const columns = args.columns || 3;
    const gapX = args.gapX != null ? args.gapX : 20;
    const gapY = args.gapY != null ? args.gapY : 20;
    const startX = args.startX != null ? args.startX : 50;
    const startY = args.startY != null ? args.startY : 50;
    let col = 0;
    let row = 0;
    let rowHeight = 0;
    ids.forEach((id, i) => {
      const comp = findComponentByRef(ctx.store, id);
      if (!comp) return;
      const w = comp.style.width || 100;
      const h = comp.style.height || 34;
      const left = startX + col * (w + gapX);
      const top = startY + row * (rowHeight + gapY);
      selectComponent(ctx.store, id, ctx);
      ctx.store.commit('setShapeStyle', { left, top });
      rowHeight = Math.max(rowHeight, h);
      col++;
      if (col >= columns) {
        col = 0;
        row++;
        rowHeight = 0;
      }
    });
    return ok({ placed: ids.length });
  },

  // --- Style ---
  'style.set_attr'(args, ctx) {
    const sel = requireSelected(ctx.store, args.ref, ctx);
    if (sel.error) return sel.error;
    setNested(sel.comp, args.path, args.value);
    return ok({});
  },
  'style.set_position_common'(args, ctx) {
    const sel = requireSelected(ctx.store, args.ref, ctx);
    if (sel.error) return sel.error;
    ctx.store.commit('setShapeStyle', {
      top: args.top,
      left: args.left,
      width: args.width,
      height: args.height,
      rotate: args.rotate,
    });
    if (args.opacity != null) sel.comp.style.opacity = args.opacity;
    return ok({});
  },
  'style.set_button_text'(args, ctx) {
    const sel = requireSelected(ctx.store, args.ref, ctx);
    if (sel.error) return sel.error;
    sel.comp.propValue = args.text;
    return ok({});
  },
  'style.set_button_colors'(args, ctx) {
    const sel = requireSelected(ctx.store, args.ref, ctx);
    if (sel.error) return sel.error;
    if (args.color != null) sel.comp.style.color = args.color;
    if (args.backgroundColor != null) sel.comp.style.backgroundColor = args.backgroundColor;
    return ok({});
  },
  'style.set_button_press_colors'(args, ctx) {
    const sel = requireSelected(ctx.store, args.ref, ctx);
    if (sel.error) return sel.error;
    if (args.pressBackgroundColor != null) {
      sel.comp.style.pressBackgroundColor = args.pressBackgroundColor;
    }
    return ok({});
  },
  'style.set_button_background_type'(args, ctx) {
    const sel = requireSelected(ctx.store, args.ref, ctx);
    if (sel.error) return sel.error;
    sel.comp.style.backgroundType = args.backgroundType;
    if (args.pressBackgroundType != null) {
      sel.comp.style.pressBackgroundType = args.pressBackgroundType;
    }
    return ok({});
  },
  'style.set_button_font'(args, ctx) {
    const sel = requireSelected(ctx.store, args.ref, ctx);
    if (sel.error) return sel.error;
    if (args.fontSize != null) sel.comp.style.fontSize = args.fontSize;
    if (args.fontFamily != null) sel.comp.style.fontFamily = args.fontFamily;
    return ok({});
  },
  'style.set_button_alias'(args, ctx) {
    const sel = requireSelected(ctx.store, args.ref, ctx);
    if (sel.error) return sel.error;
    sel.comp.style.alias = args.alias;
    return ok({});
  },
  'style.set_button_one_click'(args, ctx) {
    const sel = requireSelected(ctx.store, args.ref, ctx);
    if (sel.error) return sel.error;
    sel.comp.style.oneClick = !!args.oneClick;
    return ok({});
  },
  'style.batch_set'(args, ctx) {
    ctx.store.commit('batchSetComponentAttr', args.updates || {});
    return ok({});
  },
};

export { HANDLERS, ok, fail, requireSelected, findComponentByRef, applyPatch, getBack };
